import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import {
  faUser,
  faKey,
  faBuilding,
  faLocationDot,
  faPalette,
  faPenToSquare,
} from '@fortawesome/free-solid-svg-icons';
import { toast } from '@/lib/swal';
import { CUSTOMER_PORTAL_ROUTES } from '@/routes/customer-portal-routes';
import type { CustomerMeProfile, UpdateMyContactPayload } from '@/types/customer-auth.types';
import { CustomerPageContainer } from '@/components/customer-portal/CustomerPageContainer';
import { CustomerPageHeader } from '@/components/customer-portal/CustomerPageHeader';
import { CustomerSectionCard } from '@/components/customer-portal/CustomerSectionCard';
import { CustomerKeyValue } from '@/components/customer-portal/CustomerKeyValue';
import { CustomerButton } from '@/components/customer-portal/CustomerButton';
import { CustomerErrorState } from '@/components/customer-portal/CustomerErrorState';
import { CustomerFormField } from '@/components/customer-portal/CustomerFormField';
import { CustomerInput } from '@/components/customer-portal/CustomerInput';
import { CustomerSkeletonDetail } from '@/components/customer-portal/skeletons/CustomerSkeleton';
import { useMyAccount } from './use-my-account';

/**
 * User Profile P2 — "My account" for a customer-portal user.
 *
 * Four concepts, kept distinct (spec §4.4): the contact record ("Your
 * details"), the login identity ("Sign-in"), the customer organisation and the
 * D-130 location access — the last three read-only, changed only by the
 * service provider. What is editable is decided by the server's
 * `capabilities` (the field policy + the `customer-portal:profile:update`
 * grant); this page renders it and never decides it. Login identifiers
 * (email, phone, username) are read-only on every role: changing one is a
 * staff act that ends the sessions.
 *
 * Customer tokens only — no staff `components/shared` primitive is imported.
 */

const MANAGED_NOTE = 'Managed by your service provider.';
type EditableKey = 'alternatePhone' | 'title' | 'designation' | 'department';
const EDITABLE: EditableKey[] = ['alternatePhone', 'title', 'designation', 'department'];

const fmtDate = (iso: string | null | undefined): string =>
  iso ? new Date(iso).toLocaleString() : '—';

function joinAddress(parts: Array<string | null | undefined>, fallback?: string | null): string {
  const joined = parts.filter((p): p is string => !!p && p.trim().length > 0).join(', ');
  return joined || fallback?.trim() || '—';
}

/** The backend joins class-validator messages with ", "; each starts with the field name. */
function fieldErrorsOf(err: unknown): Record<string, string> {
  const out: Record<string, string> = {};
  if (!axios.isAxiosError(err)) return out;
  const body = err.response?.data as { error?: { message?: unknown } } | undefined;
  const message = body?.error?.message;
  const list = Array.isArray(message)
    ? (message as unknown[])
    : typeof message === 'string'
      ? message.split(', ')
      : [];
  for (const m of list) {
    if (typeof m !== 'string') continue;
    const field = m.split(' ')[0] ?? '';
    if ((EDITABLE as string[]).includes(field)) out[field] = m;
  }
  return out;
}

function initialsOf(first: string, last: string): string {
  return (
    `${first} ${last}`
      .trim()
      .split(' ')
      .slice(0, 2)
      .map((w) => w[0] ?? '')
      .join('')
      .toUpperCase() || 'C'
  );
}

export function CustomerAccountPage() {
  const { query, save } = useMyAccount();
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState<Record<EditableKey, string>>({
    alternatePhone: '',
    title: '',
    designation: '',
    department: '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  if (query.isPending) {
    return (
      <CustomerPageContainer>
        <CustomerPageHeader title="My account" />
        <div className="mt-4" data-testid="account-skeleton">
          <CustomerSkeletonDetail />
        </div>
      </CustomerPageContainer>
    );
  }

  if (query.isError || !query.data) {
    return (
      <CustomerPageContainer>
        <CustomerPageHeader title="My account" />
        <div className="mt-4">
          <CustomerErrorState
            message="We could not load your account details."
            onRetry={() => void query.refetch()}
          />
        </div>
      </CustomerPageContainer>
    );
  }

  const me: CustomerMeProfile = query.data;
  const canEdit = me.capabilities.canEditContact;
  const displayName = `${me.firstName} ${me.lastName}`.trim();

  const startEdit = () => {
    setForm({
      alternatePhone: me.alternatePhone ?? '',
      title: me.title ?? '',
      designation: me.designation ?? '',
      department: me.department ?? '',
    });
    setErrors({});
    setEditing(true);
  };

  const cancelEdit = () => {
    setEditing(false);
    setErrors({});
  };

  const validate = (): Record<string, string> => {
    const e: Record<string, string> = {};
    if (form.alternatePhone && !/^[+0-9 ()-]*$/.test(form.alternatePhone)) {
      e.alternatePhone = 'Use digits, spaces, +, ( ) and - only';
    }
    if (form.alternatePhone.length > 50) e.alternatePhone = 'At most 50 characters';
    for (const k of ['title', 'designation', 'department'] as const) {
      if (form[k].length > 100) e[k] = 'At most 100 characters';
    }
    return e;
  };

  const submit = async (ev: FormEvent) => {
    ev.preventDefault();
    const clientErrors = validate();
    if (Object.keys(clientErrors).length > 0) {
      setErrors(clientErrors);
      return;
    }
    // Exactly the four keys, always — blanks clear the value server-side.
    const payload: UpdateMyContactPayload = {};
    for (const k of EDITABLE) payload[k] = form[k].trim() === '' ? null : form[k].trim();
    try {
      await save.mutateAsync(payload);
      setEditing(false);
      setErrors({});
      toast.success('Your details were saved');
    } catch (err) {
      const fields = fieldErrorsOf(err);
      if (Object.keys(fields).length > 0) setErrors(fields);
      else toast.error('We could not save your details. Please try again.');
    }
  };

  const homeAddress = me.location
    ? joinAddress(
        [
          me.location.addressLine1,
          me.location.addressLine2,
          me.location.city,
          me.location.state,
          me.location.postalCode,
          me.location.country,
        ],
        me.location.address,
      )
    : '—';

  return (
    <CustomerPageContainer>
      <CustomerPageHeader
        title="My account"
        subtitle="Your details, how you sign in, and what your service provider has set up for you."
      />

      <div className="mt-4 space-y-4">
        {/* ── Your details ─────────────────────────────────────────────── */}
        <CustomerSectionCard
          title="Your details"
          icon={faUser}
          description={canEdit ? 'Keep your contact details up to date.' : MANAGED_NOTE}
          data-testid="account-details"
          actions={
            canEdit && !editing ? (
              <CustomerButton
                variant="outline"
                size="sm"
                icon={faPenToSquare}
                onClick={startEdit}
                aria-label="Edit your details"
              >
                Edit
              </CustomerButton>
            ) : undefined
          }
        >
          <div className="mb-4 flex items-center gap-3">
            <span
              className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-customer-primary text-sm font-semibold text-customer-action-ink"
              aria-hidden="true"
            >
              {initialsOf(me.firstName, me.lastName)}
            </span>
            <div className="min-w-0">
              <p className="truncate text-base font-semibold text-customer-text">{displayName}</p>
              {me.roleName && (
                <p className="text-xs font-medium text-customer-primary-text">{me.roleName}</p>
              )}
            </div>
          </div>

          {editing ? (
            <form
              onSubmit={submit}
              noValidate
              className="space-y-4"
              data-testid="account-edit-form"
            >
              <CustomerFormField label="Title" htmlFor="acct-title" error={errors.title}>
                <CustomerInput
                  id="acct-title"
                  value={form.title}
                  onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                  maxLength={100}
                  invalid={!!errors.title}
                  aria-describedby={errors.title ? 'acct-title-error' : undefined}
                  disabled={save.isPending}
                />
              </CustomerFormField>
              <CustomerFormField
                label="Designation"
                htmlFor="acct-designation"
                error={errors.designation}
              >
                <CustomerInput
                  id="acct-designation"
                  value={form.designation}
                  onChange={(e) => setForm((f) => ({ ...f, designation: e.target.value }))}
                  maxLength={100}
                  invalid={!!errors.designation}
                  aria-describedby={errors.designation ? 'acct-designation-error' : undefined}
                  disabled={save.isPending}
                />
              </CustomerFormField>
              <CustomerFormField
                label="Department"
                htmlFor="acct-department"
                error={errors.department}
              >
                <CustomerInput
                  id="acct-department"
                  value={form.department}
                  onChange={(e) => setForm((f) => ({ ...f, department: e.target.value }))}
                  maxLength={100}
                  invalid={!!errors.department}
                  aria-describedby={errors.department ? 'acct-department-error' : undefined}
                  disabled={save.isPending}
                />
              </CustomerFormField>
              <CustomerFormField
                label="Alternate phone"
                htmlFor="acct-alternate-phone"
                error={errors.alternatePhone}
                hint="A second number your service provider can reach you on."
              >
                <CustomerInput
                  id="acct-alternate-phone"
                  type="tel"
                  inputMode="tel"
                  value={form.alternatePhone}
                  onChange={(e) => setForm((f) => ({ ...f, alternatePhone: e.target.value }))}
                  maxLength={50}
                  invalid={!!errors.alternatePhone}
                  aria-describedby={
                    errors.alternatePhone
                      ? 'acct-alternate-phone-error'
                      : 'acct-alternate-phone-hint'
                  }
                  disabled={save.isPending}
                />
              </CustomerFormField>
              <p className="text-xs text-customer-text-secondary">
                Your name, phone and email are login and contact identifiers set by your service
                provider — ask them to change those.
              </p>
              <div className="flex flex-col gap-2 sm:flex-row sm:justify-end">
                <CustomerButton variant="outline" onClick={cancelEdit} disabled={save.isPending}>
                  Cancel
                </CustomerButton>
                <CustomerButton type="submit" loading={save.isPending} loadingLabel="Saving…">
                  Save
                </CustomerButton>
              </div>
            </form>
          ) : (
            <CustomerKeyValue
              layout="grid"
              data-testid="account-details-rows"
              items={[
                { label: 'First name', value: me.firstName },
                { label: 'Last name', value: me.lastName },
                { label: 'Title', value: me.title },
                { label: 'Designation', value: me.designation },
                { label: 'Department', value: me.department },
                {
                  label: 'Phone',
                  value: (
                    <span>
                      {me.phone ?? '—'}
                      <span className="block text-xs font-normal text-customer-text-secondary">
                        {MANAGED_NOTE}
                      </span>
                    </span>
                  ),
                },
                { label: 'Alternate phone', value: me.alternatePhone },
              ]}
            />
          )}
        </CustomerSectionCard>

        {/* ── Sign-in ──────────────────────────────────────────────────── */}
        <CustomerSectionCard
          title="Sign-in"
          icon={faKey}
          description="How you access the portal."
          data-testid="account-signin"
        >
          <CustomerKeyValue
            layout="grid"
            data-testid="account-signin-rows"
            items={[
              { label: 'Username', value: me.username },
              {
                label: 'Email',
                value: (
                  <span>
                    {me.email ?? '—'}
                    <span className="block text-xs font-normal text-customer-text-secondary">
                      {MANAGED_NOTE}
                    </span>
                  </span>
                ),
              },
              { label: 'Role', value: me.roleName },
              { label: 'Last sign-in', value: fmtDate(me.lastLoginAt) },
              { label: 'Password last changed', value: fmtDate(me.passwordChangedAt) },
            ]}
          />
          <div className="mt-4">
            <Link
              to={CUSTOMER_PORTAL_ROUTES.CHANGE_PASSWORD}
              className="customer-pressable inline-flex min-h-11 items-center justify-center gap-2 rounded-customer-sm bg-customer-primary/10 px-4 text-sm font-medium text-customer-primary-text hover:bg-customer-primary/15 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-customer-ring"
            >
              Change password
            </Link>
            <p className="mt-2 text-xs text-customer-text-secondary">
              Changing your password signs you out of every device.
            </p>
          </div>
        </CustomerSectionCard>

        {/* ── Your organisation ────────────────────────────────────────── */}
        <CustomerSectionCard
          title="Your organisation"
          icon={faBuilding}
          description={MANAGED_NOTE}
          data-testid="account-organisation"
        >
          <CustomerKeyValue
            layout="grid"
            items={[
              { label: 'Name', value: me.customerName },
              { label: 'Code', value: me.customer.code, hideWhenEmpty: true },
              {
                label: 'Registered name',
                value: me.customer.organizationName,
                hideWhenEmpty: true,
              },
              {
                label: 'Address',
                value: joinAddress(
                  [
                    me.customer.addressLine1,
                    me.customer.addressLine2,
                    me.customer.city,
                    me.customer.state,
                    me.customer.postalCode,
                    me.customer.country,
                  ],
                  me.customer.address,
                ),
              },
              { label: 'GST number', value: me.customer.gstNumber, hideWhenEmpty: true },
              { label: 'Website', value: me.customer.website, hideWhenEmpty: true },
            ]}
          />
        </CustomerSectionCard>

        {/* ── Your locations (D-130) ───────────────────────────────────── */}
        <CustomerSectionCard
          title="Your locations"
          icon={faLocationDot}
          description={
            me.locationScope === 'ORGANIZATION'
              ? 'You can see every location of your organisation. Changed by your service provider.'
              : 'You can see your own location only. Changed by your service provider.'
          }
          data-testid="account-locations"
        >
          <CustomerKeyValue
            layout="grid"
            items={[
              { label: 'Home location', value: me.location?.name ?? '—' },
              { label: 'Address', value: homeAddress },
            ]}
          />
          {me.locationScope === 'ORGANIZATION' && me.locations && (
            <ul
              className="mt-4 divide-y divide-customer-border rounded-customer-sm border border-customer-border"
              aria-label="All locations"
              data-testid="account-all-locations"
            >
              {me.locations.map((l) => (
                <li key={l.id} className="px-3 py-2.5 text-sm text-customer-text">
                  {l.name}
                </li>
              ))}
            </ul>
          )}
        </CustomerSectionCard>

        {/* ── Appearance ───────────────────────────────────────────────── */}
        <CustomerSectionCard
          title="Appearance"
          icon={faPalette}
          description="Theme and light / dark mode, saved on this device."
          data-testid="account-appearance"
        >
          <Link
            to={CUSTOMER_PORTAL_ROUTES.APPEARANCE}
            className="customer-pressable inline-flex min-h-11 items-center justify-center rounded-customer-sm border border-customer-border bg-customer-surface px-4 text-sm font-medium text-customer-text hover:bg-customer-surface-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-customer-ring"
          >
            Open appearance settings
          </Link>
        </CustomerSectionCard>
      </div>
    </CustomerPageContainer>
  );
}
